function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import { QueryReportHeaderReact } from 'common/query-builder/query-report-header';
import { HeaderAction, QueryReportHeaderParams } from 'common/query-builder/query-report-header/types';
import { buildQueryReportHeaderParams, getEntityType, getEntityTypeName, getEntityTypeWithContentSupport, normalizeContentEntity } from 'common/report/util/saved-entities';
import { Content } from 'common/types/app-api/content';
import { TrackingSource } from 'common/content/tracking';
import { getContentHomeUrl, getSearchDisplayContext } from 'common/content/helpers';
import { useTranslation } from 'common/i18n/hooks';
import { useRenameContentWithSidenav } from 'common/content/hooks/useRenameContentWithSidenav';
import { useFavoriteContentWithSidenav } from 'common/content/hooks/useFavoriteContentWithSidenav';
import { usePinContentWithSidenav } from 'common/content/hooks/usePinContentWithSidenav';
import { useDeleteContentWithSidenav } from 'common/content/hooks/useDeleteContentWithSidenav';
import { useDuplicateContentWithSidenav } from 'common/content/hooks/useDuplicateContentWithSidenav';
import { EntityType, SharableEntityType } from 'common/report/util/saved-entities/types';
import styles from './index.module.styl';
import { CustomHeaderAction } from './types';
import { ContentFavoriteButton } from './favorite-btn';
type ContentPageHeaderProps = {
  projectId: number;
  workspaceId: number;
  userId: number;
  content?: Content;
  hasUnsavedChanges?: boolean;
  onUpdate?: (content: Content) => void;
  onCommit?: () => void;
  onRevert?: () => void;
};
export function ContentPageHeader(_ref: ContentPageHeaderProps) {
  var _content$content_type;
  let {
    projectId,
    workspaceId,
    userId,
    content,
    hasUnsavedChanges,
    onUpdate,
    onCommit,
    onRevert
  }: ContentPageHeaderProps = _ref;
  const i18n = useTranslation();
  const contentEntity = normalizeContentEntity(content);
  const renameContent = useRenameContentWithSidenav({
    projectId,
    source: TrackingSource.Header
  });
  const favoriteContent = useFavoriteContentWithSidenav({
    projectId,
    workspaceId,
    source: TrackingSource.Header
  });
  const pinContent = usePinContentWithSidenav({
    projectId,
    workspaceId,
    userId,
    source: TrackingSource.Header
  });
  const deleteContent = useDeleteContentWithSidenav({
    projectId,
    workspaceId,
    returnHomeAfterSuccess: true,
    source: TrackingSource.Header
  });
  const duplicateContent = useDuplicateContentWithSidenav({
    projectId,
    workspaceId,
    redirectAfterSuccess: true,
    source: TrackingSource.Header
  });
  const homeUrl = content ? getContentHomeUrl(content.content_type!, {
    projectId,
    workspaceId
  }) : undefined;
  const homePage = homeUrl ? {
    title: i18n.print(getEntityTypeName(getEntityType((_content$content_type = content === null || content === void 0 ? void 0 : content.content_type) !== null && _content$content_type !== void 0 ? _content$content_type : SharableEntityType.Report), {
      plural: true,
      titleCased: true
    })),
    url: homeUrl
  } : undefined;
  const queryReportHeaderParams: QueryReportHeaderParams = React.useMemo(() => {
    const entityType = content && getEntityTypeWithContentSupport(content.content_type!) || EntityType.Report;
    return _objectSpread(_objectSpread({}, buildQueryReportHeaderParams({
      projectId,
      entityType,
      entity: contentEntity!,
      invalidEntity: !(contentEntity !== null && contentEntity !== void 0 && contentEntity.id),
      displayContext: getSearchDisplayContext(content!)
    })), {}, {
      homePage,
      entityType,
      headerActionItemsEl: ContentFavoriteButton({
        isLoading: !content,
        isSelected: content === null || content === void 0 ? void 0 : content.is_favorited,
        i18n,
        tooltipTranslationKeys: {
          favorite: "sidenav.favorite",
          unfavorite: "sidenav.unfavorite"
        },
        onClick: async () => {
          const result = await favoriteContent(content!);
          if (result) onUpdate === null || onUpdate === void 0 || onUpdate(result);
        }
      }),
      trackName: "",
      overflowControlItems: [{
        items: [...(content !== null && content !== void 0 && content.can_pin ? [{
          name: CustomHeaderAction.PinContent,
          label: content !== null && content !== void 0 && content.pinned_date ? i18n.t("sidenav.unpin") : i18n.t("sidenav.pin"),
          icon: content !== null && content !== void 0 && content.pinned_date ? "filled-pin" : "pin",
          highlightIcon: Boolean(content === null || content === void 0 ? void 0 : content.pinned_date)
        }] : [])]
      }],
      controlItems: [{
        slot: "favoriteButton"
      }],
      invalidMessage: "",
      projectId,
      workspaceId,
      userId,
      entitiesLoading: !content,
      hasContentSaveButtons: Boolean(onCommit || onRevert),
      hasUnsavedChanges: Boolean(hasUnsavedChanges)
    }) satisfies QueryReportHeaderParams;
  }, [projectId, workspaceId, userId, content, contentEntity, favoriteContent, i18n]);
  const handleHeaderActionClick = React.useCallback(async (ev: CustomEvent) => {
    const actionName = ev.detail.name || ev.detail.value;
    switch (actionName) {
      case HeaderAction.Delete:
        deleteContent(content!);
        break;
      case HeaderAction.Duplicate:
        duplicateContent(content!);
        break;
      case CustomHeaderAction.PinContent:
        {
          const result = await pinContent(content!);
          if (result) onUpdate === null || onUpdate === void 0 || onUpdate(result);
          break;
        }
    }
  }, [deleteContent, duplicateContent, pinContent, content]);
  const handleSave = React.useCallback(async (ev: CustomEvent<{
    name: string;
  }>) => {
    const newName = ev.detail.name;
    const result = await renameContent(content!, {
      name: newName,
      description: (content === null || content === void 0 ? void 0 : content.description) || ""
    });
    if (result) onUpdate === null || onUpdate === void 0 || onUpdate(result);
  }, [content, renameContent]);
  const handleCommit = React.useCallback(() => {
    if (onCommit) {
      onCommit();
    }
  }, [onCommit]);
  const handleRevert = React.useCallback(() => {
    if (onRevert) {
      onRevert();
    }
  }, [onRevert]);
  return <div className={styles.container} data-sentry-component="ContentPageHeader" data-sentry-source-file="index.tsx">
      <QueryReportHeaderReact {...queryReportHeaderParams} onActionclick={handleHeaderActionClick} onSave={handleSave} onContentSave={handleCommit} onContentCancel={handleRevert} data-sentry-element="QueryReportHeaderReact" data-sentry-source-file="index.tsx" />
    </div>;
}
//